/**
 * Custom storage adapter for Supabase auth that supports true remember-me persistence.
 * 
 * When remember-me is DISABLED (session-only mode):
 * - Sets a flag in sessionStorage
 * - All auth tokens are stored in sessionStorage
 * - When browser closes, sessionStorage is cleared → user is logged out
 * 
 * When remember-me is ENABLED (persistent mode):
 * - No flag set
 * - All auth tokens are stored in localStorage
 * - Session persists across browser restarts
 */

import { SUPABASE_PROJECT_ID } from './config';

const SESSION_ONLY_FLAG = 'lawassist_session_only';

// Auth key derived from project ID
export const AUTH_STORAGE_KEY = `sb-${SUPABASE_PROJECT_ID}-auth-token`;

/**
 * Check if we're in a browser environment
 */
function isBrowser(): boolean {
  return typeof globalThis !== 'undefined';
}

/**
 * Check if we're in session-only mode
 */
function isSessionOnly(): boolean {
  if (!isBrowser()) return false;
  const session = (globalThis as typeof globalThis & { sessionStorage?: Storage }).sessionStorage;
  return session?.getItem(SESSION_ONLY_FLAG) === 'true';
}

/**
 * Get the active storage based on current mode
 */
function getActiveStorage(): Storage | null {
  if (!isBrowser()) return null;
  const local = (globalThis as typeof globalThis & { localStorage?: Storage }).localStorage;
  const session = (globalThis as typeof globalThis & { sessionStorage?: Storage }).sessionStorage;
  if (!local && !session) return null;
  return isSessionOnly() ? session ?? local ?? null : local ?? session ?? null;
}

/**
 * Custom storage adapter that routes to sessionStorage or localStorage
 * based on the session-only flag.
 */
class RememberMeStorage implements Storage {
  get length(): number {
    const storage = getActiveStorage();
    if (!storage) return 0;
    if (typeof storage.length === 'number') return storage.length;
    return Object.keys(storage).filter((key) => typeof (storage as Record<string, unknown>)[key] !== 'function').length;
  }

  key(index: number): string | null {
    const storage = getActiveStorage();
    if (!storage) return null;
    if (typeof storage.key === 'function') {
      return storage.key(index);
    }
    const keys = Object.keys(storage).filter((key) => typeof (storage as Record<string, unknown>)[key] !== 'function');
    return keys[index] ?? null;
  }

  getItem(key: string): string | null {
    if (!isBrowser()) return null;
    
    // In session-only mode, read from sessionStorage
    if (isSessionOnly()) {
      const session = (globalThis as typeof globalThis & { sessionStorage?: Storage }).sessionStorage;
      return session?.getItem(key) ?? null;
    }
    const local = (globalThis as typeof globalThis & { localStorage?: Storage }).localStorage;
    return local?.getItem(key) ?? null;
  }

  setItem(key: string, value: string): void {
    if (!isBrowser()) return;
    
    // Write to the active storage
    const storage = getActiveStorage();
    if (storage) {
      storage.setItem(key, value);
    }
  }

  removeItem(key: string): void {
    if (!isBrowser()) return;
    
    // Remove from both to ensure cleanup
    const local = (globalThis as typeof globalThis & { localStorage?: Storage }).localStorage;
    const session = (globalThis as typeof globalThis & { sessionStorage?: Storage }).sessionStorage;
    local?.removeItem(key);
    session?.removeItem(key);
  }

  clear(): void {
    if (!isBrowser()) return;
    
    // Clear auth-related items from active storage
    const storage = getActiveStorage();
    if (storage) {
      storage.clear();
    }
  }
}

// Export singleton instance for use in Supabase client
export const rememberMeStorage = new RememberMeStorage();

/**
 * Apply remember-me preference after successful sign-in.
 * Call this AFTER supabase.auth.signInWithPassword() succeeds.
 * 
 * @param rememberMe - If true, persist session; if false, session-only
 */
export function applyRememberMePreference(rememberMe: boolean): void {
  if (!isBrowser()) return;

  if (!rememberMe) {
    // Switch to session-only mode
    const session = (globalThis as typeof globalThis & { sessionStorage?: Storage }).sessionStorage;
    const local = (globalThis as typeof globalThis & { localStorage?: Storage }).localStorage;
    session?.setItem(SESSION_ONLY_FLAG, 'true');
    
    // Migrate tokens from localStorage to sessionStorage
    const tokens = local?.getItem(AUTH_STORAGE_KEY);
    if (tokens) {
      session?.setItem(AUTH_STORAGE_KEY, tokens);
      local?.removeItem(AUTH_STORAGE_KEY);
    }
  } else {
    // Switch to persistent mode
    const session = (globalThis as typeof globalThis & { sessionStorage?: Storage }).sessionStorage;
    const local = (globalThis as typeof globalThis & { localStorage?: Storage }).localStorage;
    session?.removeItem(SESSION_ONLY_FLAG);
    
    // Migrate tokens from sessionStorage to localStorage if needed
    const sessionTokens = session?.getItem(AUTH_STORAGE_KEY);
    if (sessionTokens && local && !local.getItem(AUTH_STORAGE_KEY)) {
      local.setItem(AUTH_STORAGE_KEY, sessionTokens);
      session?.removeItem(AUTH_STORAGE_KEY);
    }
  }
}

/**
 * Clear all auth storage on sign-out.
 * Call this when the user signs out to ensure clean state.
 */
export function clearAuthStorageOnSignOut(): void {
  if (!isBrowser()) return;

  // Clear the session-only flag
  const session = (globalThis as typeof globalThis & { sessionStorage?: Storage }).sessionStorage;
  const local = (globalThis as typeof globalThis & { localStorage?: Storage }).localStorage;
  session?.removeItem(SESSION_ONLY_FLAG);
  
  // Clear auth tokens from both storages
  local?.removeItem(AUTH_STORAGE_KEY);
  session?.removeItem(AUTH_STORAGE_KEY);
}

/**
 * Check if we're in session-only mode (for UI purposes)
 */
export function isSessionOnlyMode(): boolean {
  return isSessionOnly();
}
